/**
 * SettingsPanel Component - Versione Semplice
 * Layout a lista verticale (come era prima)
 */

import { useState } from "react";
import { 
  User, 
  Users, 
  Bell, 
  Shield, 
  FileText, 
  Smartphone,
  Palette,
  ChevronRight,
  LogOut,
  Moon,
  Sun
} from "lucide-react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/useAuth";
import { TeamList } from "@/features/collaboratori";
import { ContrattoTemplateList } from "@/features/legal";
import { useGuestAppConfig } from "@/features/configurazione";

export default function SettingsPanel() {
  const [activeTab, setActiveTab] = useState("profilo");
  const { currentUser, logout } = useAuth();
  const { theme, isDark, themes, setTheme, toggleMode } = useTheme();
  const { adminId, isLoading: loadingGuestApp } = useGuestAppConfig();

  const TABS = [
    { id: "profilo", label: "Profilo", icon: User, desc: "Dati personali e account" },
    { id: "team", label: "Team", icon: Users, desc: "Gestione membri e ruoli" },
    { id: "notifiche", label: "Notifiche", icon: Bell, desc: "Alert e preferenze" },
    { id: "sicurezza", label: "Sicurezza", icon: Shield, desc: "Password e 2FA" },
    { id: "contratti", label: "Contratti", icon: FileText, desc: "Template legali" },
    { id: "guestapp", label: "Guest App", icon: Smartphone, desc: "Configurazione app" },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "profilo":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Profilo</h3>
            <div className="flex items-center gap-4 pb-6 border-b">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <span className="text-2xl font-bold">{currentUser?.email?.[0]?.toUpperCase() ?? "?"}</span>
              </div>
              <div>
                <p className="font-medium">{currentUser?.email?.split("@")[0]}</p>
                <p className="text-sm text-muted-foreground">{currentUser?.email}</p>
              </div>
            </div>
            <div className="grid gap-4">
              <div className="p-4 rounded-xl bg-muted">
                <label className="block text-sm font-medium mb-2">Email</label>
                <input type="email" value={currentUser?.email || ""} disabled className="w-full px-3 py-2 rounded-lg bg-background border text-sm" />
              </div>
              <button onClick={logout} className="flex items-center gap-2 p-4 rounded-xl bg-muted text-red-500 hover:bg-red-50 transition-colors">
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium">Disconnetti</span>
              </button>
            </div>
          </div>
        );
      case "team":
        return <TeamList />;
      case "notifiche":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Notifiche</h3>
            <p className="text-muted-foreground">Gestisci alert e preferenze</p>
          </div>
        );
      case "sicurezza":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Sicurezza</h3>
            <p className="text-muted-foreground">Password e 2FA</p>
          </div>
        );
      case "contratti":
        return <ContrattoTemplateList />;
      case "guestapp":
        return loadingGuestApp ? (
          <div>Caricamento...</div>
        ) : (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Guest App</h3>
            <p className="text-muted-foreground">Configurazione app ospiti</p>
            <div className="p-4 rounded-xl bg-muted text-sm">ID Admin: {adminId || "N/A"}</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Impostazioni</h1>
      
      {/* Lista verticale di sezioni */}
      <div className="space-y-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                isActive 
                  ? "bg-card border-primary/20 shadow-sm" 
                  : "bg-card border-border hover:border-primary/10"
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className={`font-medium ${isActive ? "text-foreground" : ""}`}>{tab.label}</p>
                <p className="text-sm text-muted-foreground">{tab.desc}</p>
              </div>
              <ChevronRight className={`h-5 w-5 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
            </button>
          );
        })}
      </div>

      {/* Contenuto espanso quando selezionato */}
      <div className="mt-6 p-6 bg-card rounded-2xl border border-border">
        {renderContent()}
      </div>

      {/* Tema (in fondo) */}
      <div className="mt-6 p-6 bg-card rounded-2xl border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Tema</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Colore</label>
            <div className="flex gap-2">
              {themes.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                    theme === t.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                  }`}
                >
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-sm">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={toggleMode}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span className="text-sm">{isDark ? "Passa a chiaro" : "Passa a scuro"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
